/**
 * Actuator Device Calculations Utility
 *
 * Provides comprehensive calculations and formatting for Actuator control devices.
 * Features:
 * - Multi-state control (on/off/intermediate/transitioning)
 * - Operation history tracking
 * - Position control (0-100% for dimmers, valves, blinds)
 * - Transition time tracking
 * - Automation trigger tracking
 * - Usage statistics and patterns
 * - Error/fault detection
 * - Multi-language support (Arabic/English)
 *
 * @version 2.4.0
 * @date January 23, 2026
 */

// ==================== Type Definitions ====================

/**
 * Actuator state types
 */
export type ActuatorState = 'on' | 'off' | 'intermediate' | 'transitioning' | 'error';

/**
 * Actuator operation event from the device
 */
export interface ActuatorEvent {
  timestamp: Date;
  state: ActuatorState; // Current state
  position?: number; // Position 0-100% (for dimmers, valves, blinds)
  targetPosition?: number; // Target position during transition
  transitionTime?: number; // Transition duration in seconds
  triggeredBy?: 'manual' | 'schedule' | 'automation' | 'remote' | 'sensor';
  reason?: string; // Automation reason (e.g., "temperature threshold")
  error?: string; // Error message if state is 'error'
}

/**
 * Operation history item
 */
export interface OperationHistoryItem {
  timestamp: Date;
  action: string; // Description of the action
  state: ActuatorState;
  position?: number;
  triggeredBy?: string;
  duration?: number; // Duration of transition in seconds
  success: boolean;
}

/**
 * Actuator usage pattern for a specific hour
 */
export interface ActuatorHourPattern {
  hour: number; // Hour of day (0-23)
  operationCount: number; // Number of operations in this hour
  onDuration: number; // Minutes in ON state
  intermediateDuration: number; // Minutes in intermediate state
  averagePosition: number; // Average position (0-100)
  isPeakHour: boolean; // True if this is peak usage hour
}

/**
 * Actuator timeline item
 */
export interface ActuatorTimelineItem {
  timestamp: Date;
  state: ActuatorState;
  position?: number;
  description: string;
  triggeredBy?: string;
  icon: string; // Icon name for display
}

/**
 * Comprehensive actuator statistics
 */
export interface ActuatorStats {
  totalOperations: number; // Total state changes
  onTime: number; // Total minutes in ON state
  offTime: number; // Total minutes in OFF state
  intermediateTime: number; // Total minutes in intermediate state
  averagePosition: number; // Average position (0-100)
  mostCommonPosition: number; // Most frequent position
  utilizationRate: number; // Percentage of time active (0-100)
  manualOperations: number; // User-initiated operations
  autoOperations: number; // Automation-initiated operations
  failedOperations: number; // Failed operations count
  averageTransitionTime: number; // Average transition duration (seconds)
  mostActiveHour: number; // Hour with most operations
  leastActiveHour: number; // Hour with least operations
}

/**
 * Real-time actuator status
 */
export interface ActuatorStatus {
  currentState: ActuatorState;
  currentPosition: number; // Current position 0-100%
  targetPosition?: number; // Target position if transitioning
  isActive: boolean; // True if on or intermediate
  lastOperation?: Date; // Last state change time
  todayOperations: number; // Operations today
  todayOnTime: number; // Minutes ON today
  todayIntermediateTime: number; // Minutes intermediate today
  yesterdayOperations: number; // Operations yesterday
  weekOperations: number; // Operations this week
  monthOperations: number; // Operations this month
  estimatedCompletion?: Date; // ETA if transitioning
  lastError?: string; // Last error message
  healthStatus: 'good' | 'warning' | 'critical'; // Device health
  automationEnabled: boolean; // Automation status
}

// ==================== Core Calculation Functions ====================

/**
 * Count total operations (state changes) in a time period
 *
 * @param events - Array of actuator events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Total number of operations
 */
export function countOperations(events: ActuatorEvent[], startDate?: Date, endDate?: Date): number {
  if (!events || events.length === 0) return 0;

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  // Count state changes
  let operations = 0;
  let lastState: ActuatorState | null = null;

  for (const event of filteredEvents) {
    if (lastState !== null && event.state !== lastState) {
      operations++;
    }
    lastState = event.state;
  }

  return operations;
}

/**
 * Calculate time spent in each state
 *
 * @param events - Array of actuator events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Object with minutes in each state
 */
export function calculateStateDurations(
  events: ActuatorEvent[],
  startDate?: Date,
  endDate?: Date,
): { onTime: number; offTime: number; intermediateTime: number; errorTime: number } {
  if (!events || events.length === 0) {
    return { onTime: 0, offTime: 0, intermediateTime: 0, errorTime: 0 };
  }

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  if (filteredEvents.length === 0) {
    return { onTime: 0, offTime: 0, intermediateTime: 0, errorTime: 0 };
  }

  const sortedEvents = [...filteredEvents].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  let onTime = 0;
  let offTime = 0;
  let intermediateTime = 0;
  let errorTime = 0;

  for (let i = 0; i < sortedEvents.length - 1; i++) {
    const currentEvent = sortedEvents[i];
    const nextEvent = sortedEvents[i + 1];
    const durationMinutes =
      (nextEvent.timestamp.getTime() - currentEvent.timestamp.getTime()) / (1000 * 60);

    switch (currentEvent.state) {
      case 'on':
        onTime += durationMinutes;
        break;
      case 'off':
        offTime += durationMinutes;
        break;
      case 'intermediate':
        intermediateTime += durationMinutes;
        break;
      case 'error':
        errorTime += durationMinutes;
        break;
    }
  }

  // Handle last event (count until end date)
  const lastEvent = sortedEvents[sortedEvents.length - 1];
  const lastDuration = (end.getTime() - lastEvent.timestamp.getTime()) / (1000 * 60);

  switch (lastEvent.state) {
    case 'on':
      onTime += lastDuration;
      break;
    case 'off':
      offTime += lastDuration;
      break;
    case 'intermediate':
      intermediateTime += lastDuration;
      break;
    case 'error':
      errorTime += lastDuration;
      break;
  }

  return {
    onTime: Math.round(onTime),
    offTime: Math.round(offTime),
    intermediateTime: Math.round(intermediateTime),
    errorTime: Math.round(errorTime),
  };
}

/**
 * Calculate average position over time period
 *
 * @param events - Array of actuator events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Average position (0-100)
 */
export function calculateAveragePosition(
  events: ActuatorEvent[],
  startDate?: Date,
  endDate?: Date,
): number {
  if (!events || events.length === 0) return 0;

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end && event.position !== undefined;
  });

  if (filteredEvents.length === 0) return 0;

  const totalPosition = filteredEvents.reduce((sum, event) => sum + (event.position || 0), 0);
  return Math.round(totalPosition / filteredEvents.length);
}

/**
 * Count operations by trigger source
 *
 * @param events - Array of actuator events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Object with counts per trigger source
 */
export function countOperationsByTrigger(
  events: ActuatorEvent[],
  startDate?: Date,
  endDate?: Date,
): { manual: number; schedule: number; automation: number; remote: number; sensor: number } {
  if (!events || events.length === 0) {
    return { manual: 0, schedule: 0, automation: 0, remote: 0, sensor: 0 };
  }

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  const counts = {
    manual: 0,
    schedule: 0,
    automation: 0,
    remote: 0,
    sensor: 0,
  };

  for (const event of filteredEvents) {
    if (event.triggeredBy) {
      counts[event.triggeredBy]++;
    }
  }

  return counts;
}

/**
 * Generate 24-hour actuator usage pattern
 *
 * @param events - Array of actuator events
 * @param date - Specific date to analyze (optional, defaults to today)
 * @returns Array of 24 hourly patterns
 */
export function getActuatorPattern(
  events: ActuatorEvent[],
  date: Date = new Date(),
): ActuatorHourPattern[] {
  const patterns: ActuatorHourPattern[] = [];

  // Initialize all 24 hours
  for (let hour = 0; hour < 24; hour++) {
    patterns.push({
      hour,
      operationCount: 0,
      onDuration: 0,
      intermediateDuration: 0,
      averagePosition: 0,
      isPeakHour: false,
    });
  }

  if (!events || events.length === 0) return patterns;

  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  // Process each hour
  for (let hour = 0; hour < 24; hour++) {
    const hourStart = new Date(targetDate);
    hourStart.setHours(hour, 0, 0, 0);
    const hourEnd = new Date(targetDate);
    hourEnd.setHours(hour, 59, 59, 999);

    const hourEvents = events.filter((event) => {
      const eventDate = new Date(event.timestamp);
      return eventDate >= hourStart && eventDate <= hourEnd;
    });

    if (hourEvents.length > 0) {
      const operationCount = countOperations(events, hourStart, hourEnd);
      const durations = calculateStateDurations(events, hourStart, hourEnd);
      const avgPosition = calculateAveragePosition(hourEvents);

      patterns[hour] = {
        hour,
        operationCount,
        onDuration: durations.onTime,
        intermediateDuration: durations.intermediateTime,
        averagePosition: avgPosition,
        isPeakHour: false,
      };
    }
  }

  // Identify peak hour (most operations)
  const maxOperations = Math.max(...patterns.map((p) => p.operationCount));
  if (maxOperations > 0) {
    const peakHourPattern = patterns.find((p) => p.operationCount === maxOperations);
    if (peakHourPattern) {
      peakHourPattern.isPeakHour = true;
    }
  }

  return patterns;
}

/**
 * Generate operation history timeline
 *
 * @param events - Array of actuator events
 * @param maxItems - Maximum number of items to return
 * @returns Array of operation history items
 */
export function getOperationHistory(
  events: ActuatorEvent[],
  maxItems: number = 10,
): OperationHistoryItem[] {
  if (!events || events.length === 0) return [];

  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const recentEvents = sortedEvents.slice(0, maxItems);

  return recentEvents.map((event) => {
    let action = '';

    switch (event.state) {
      case 'on':
        action = 'Turned ON';
        break;
      case 'off':
        action = 'Turned OFF';
        break;
      case 'intermediate':
        action = event.position !== undefined ? `Set to ${event.position}%` : 'Set to intermediate';
        break;
      case 'transitioning':
        action =
          event.targetPosition !== undefined
            ? `Transitioning to ${event.targetPosition}%`
            : 'Transitioning';
        break;
      case 'error':
        action = 'Error occurred';
        break;
    }

    const triggeredBy = event.triggeredBy || 'unknown';
    const success = event.state !== 'error';

    return {
      timestamp: event.timestamp,
      action,
      state: event.state,
      position: event.position,
      triggeredBy,
      duration: event.transitionTime,
      success,
    };
  });
}

/**
 * Generate timeline of recent actuator events
 *
 * @param events - Array of actuator events
 * @param maxItems - Maximum number of items to return
 * @returns Array of timeline items
 */
export function getActuatorTimeline(
  events: ActuatorEvent[],
  maxItems: number = 10,
): ActuatorTimelineItem[] {
  if (!events || events.length === 0) return [];

  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const recentEvents = sortedEvents.slice(0, maxItems);

  return recentEvents.map((event) => {
    let description = '';
    let icon = 'toggle';

    switch (event.state) {
      case 'on':
        description = 'Activated';
        icon = 'toggle';
        break;
      case 'off':
        description = 'Deactivated';
        icon = 'toggle-outline';
        break;
      case 'intermediate':
        description =
          event.position !== undefined ? `Position: ${event.position}%` : 'Intermediate state';
        icon = 'options';
        break;
      case 'transitioning':
        description = 'In transition';
        icon = 'sync';
        break;
      case 'error':
        description = event.error || 'Error occurred';
        icon = 'alert-circle';
        break;
    }

    if (event.triggeredBy) {
      const triggerText =
        event.triggeredBy === 'manual'
          ? 'Manual'
          : event.triggeredBy === 'schedule'
          ? 'Scheduled'
          : event.triggeredBy === 'automation'
          ? 'Automated'
          : event.triggeredBy === 'sensor'
          ? 'Sensor-triggered'
          : 'Remote';
      description += ` (${triggerText})`;
    }

    if (event.reason) {
      description += ` • ${event.reason}`;
    }

    return {
      timestamp: event.timestamp,
      state: event.state,
      position: event.position,
      description,
      triggeredBy: event.triggeredBy,
      icon,
    };
  });
}

/**
 * Calculate comprehensive actuator statistics
 *
 * @param events - Array of actuator events
 * @param startDate - Start of period
 * @param endDate - End of period
 * @returns Complete statistics object
 */
export function getActuatorStatistics(
  events: ActuatorEvent[],
  startDate: Date,
  endDate: Date,
): ActuatorStats {
  if (!events || events.length === 0) {
    return {
      totalOperations: 0,
      onTime: 0,
      offTime: 0,
      intermediateTime: 0,
      averagePosition: 0,
      mostCommonPosition: 0,
      utilizationRate: 0,
      manualOperations: 0,
      autoOperations: 0,
      failedOperations: 0,
      averageTransitionTime: 0,
      mostActiveHour: 0,
      leastActiveHour: 0,
    };
  }

  const totalOperations = countOperations(events, startDate, endDate);
  const durations = calculateStateDurations(events, startDate, endDate);
  const averagePosition = calculateAveragePosition(events, startDate, endDate);
  const triggerCounts = countOperationsByTrigger(events, startDate, endDate);

  const totalPeriodMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
  const activeTime = durations.onTime + durations.intermediateTime;
  const utilizationRate =
    totalPeriodMinutes > 0 ? Math.round((activeTime / totalPeriodMinutes) * 100) : 0;

  const manualOperations = triggerCounts.manual;
  const autoOperations = triggerCounts.automation + triggerCounts.schedule + triggerCounts.sensor;

  const failedOperations = events.filter(
    (e) =>
      e.state === 'error' && new Date(e.timestamp) >= startDate && new Date(e.timestamp) <= endDate,
  ).length;

  // Calculate average transition time
  const transitionEvents = events.filter(
    (e) =>
      e.transitionTime !== undefined &&
      new Date(e.timestamp) >= startDate &&
      new Date(e.timestamp) <= endDate,
  );
  const averageTransitionTime =
    transitionEvents.length > 0
      ? Math.round(
          transitionEvents.reduce((sum, e) => sum + (e.transitionTime || 0), 0) /
            transitionEvents.length,
        )
      : 0;

  // Find most common position
  const positions = events.filter((e) => e.position !== undefined).map((e) => e.position!);
  const positionCounts = new Map<number, number>();
  positions.forEach((pos) => {
    positionCounts.set(pos, (positionCounts.get(pos) || 0) + 1);
  });
  let mostCommonPosition = 0;
  let maxCount = 0;
  positionCounts.forEach((count, pos) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommonPosition = pos;
    }
  });

  const pattern = getActuatorPattern(events, startDate);
  const mostActive = pattern.reduce((max, curr) =>
    curr.operationCount > max.operationCount ? curr : max,
  );
  const leastActive = pattern.reduce((min, curr) =>
    curr.operationCount < min.operationCount && curr.operationCount > 0 ? curr : min,
  );

  return {
    totalOperations,
    onTime: durations.onTime,
    offTime: durations.offTime,
    intermediateTime: durations.intermediateTime,
    averagePosition,
    mostCommonPosition,
    utilizationRate,
    manualOperations,
    autoOperations,
    failedOperations,
    averageTransitionTime,
    mostActiveHour: mostActive.hour,
    leastActiveHour: leastActive.hour,
  };
}

/**
 * Get real-time actuator status
 *
 * @param events - Array of actuator events
 * @param currentState - Current actuator state
 * @param currentPosition - Current position (0-100)
 * @param targetPosition - Target position if transitioning (optional)
 * @param automationEnabled - Whether automation is enabled
 * @returns Complete actuator status
 */
export function getActuatorStatus(
  events: ActuatorEvent[],
  currentState: ActuatorState,
  currentPosition: number,
  targetPosition?: number,
  automationEnabled: boolean = true,
): ActuatorStatus {
  const now = new Date();
  const isActive = currentState === 'on' || currentState === 'intermediate';

  // Find last operation
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const lastOperation = sortedEvents.length > 0 ? sortedEvents[0].timestamp : undefined;

  // Today's data
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const todayOperations = countOperations(events, todayStart, todayEnd);
  const todayDurations = calculateStateDurations(events, todayStart, todayEnd);

  // Yesterday's data
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const yesterdayEnd = new Date(todayStart);
  yesterdayEnd.setMilliseconds(-1);
  const yesterdayOperations = countOperations(events, yesterdayStart, yesterdayEnd);

  // This week's data
  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekOperations = countOperations(events, weekStart, now);

  // This month's data
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthOperations = countOperations(events, monthStart, now);

  // Estimate completion time if transitioning
  let estimatedCompletion: Date | undefined;
  if (currentState === 'transitioning' && sortedEvents.length > 0) {
    const lastEvent = sortedEvents[0];
    if (lastEvent.transitionTime) {
      estimatedCompletion = new Date(
        lastEvent.timestamp.getTime() + lastEvent.transitionTime * 1000,
      );
    }
  }

  // Get last error
  const lastError = sortedEvents.find((e) => e.state === 'error')?.error;

  // Determine health status
  let healthStatus: 'good' | 'warning' | 'critical' = 'good';
  const recentErrors = events.filter(
    (e) =>
      e.state === 'error' && new Date(e.timestamp).getTime() > now.getTime() - 24 * 60 * 60 * 1000,
  ).length;

  if (currentState === 'error' || recentErrors >= 5) {
    healthStatus = 'critical';
  } else if (recentErrors >= 2) {
    healthStatus = 'warning';
  }

  return {
    currentState,
    currentPosition,
    targetPosition,
    isActive,
    lastOperation,
    todayOperations,
    todayOnTime: todayDurations.onTime,
    todayIntermediateTime: todayDurations.intermediateTime,
    yesterdayOperations,
    weekOperations,
    monthOperations,
    estimatedCompletion,
    lastError,
    healthStatus,
    automationEnabled,
  };
}

// ==================== Formatting Functions ====================

/**
 * Format actuator state text
 *
 * @param state - Actuator state
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized state text
 */
export function formatActuatorState(state: ActuatorState, lang: 'ar' | 'en' = 'en'): string {
  const translations: { [key in ActuatorState]: { ar: string; en: string } } = {
    on: { ar: 'مشغّل', en: 'ON' },
    off: { ar: 'متوقف', en: 'OFF' },
    intermediate: { ar: 'متوسط', en: 'Intermediate' },
    transitioning: { ar: 'جاري التحويل', en: 'Transitioning' },
    error: { ar: 'خطأ', en: 'Error' },
  };

  return translations[state]?.[lang] || state;
}

/**
 * Format position percentage
 *
 * @param position - Position (0-100)
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted position string
 */
export function formatPosition(position: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? Math.round(position)
          .toString()
          .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : Math.round(position).toString();
  return lang === 'ar' ? `${formatted}٪` : `${formatted}%`;
}

/**
 * Format operation count
 *
 * @param count - Number of operations
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted count string
 */
export function formatOperationCount(count: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? count.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : count.toString();
  return lang === 'ar' ? `${formatted} عملية` : `${formatted} ops`;
}

/**
 * Format duration in minutes to readable format
 *
 * @param minutes - Duration in minutes
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted duration string
 */
export function formatDurationActuator(minutes: number, lang: 'ar' | 'en' = 'en'): string {
  if (minutes < 60) {
    const formatted =
      lang === 'ar'
        ? Math.round(minutes)
            .toString()
            .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
        : Math.round(minutes).toString();
    return lang === 'ar' ? `${formatted} د` : `${formatted}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (lang === 'ar') {
    const hoursStr = hours.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    const minsStr = remainingMinutes.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    return `${hoursStr} س ${minsStr} د`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
}

/**
 * Format transition time in seconds
 *
 * @param seconds - Transition time in seconds
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted time string
 */
export function formatTransitionTime(seconds: number, lang: 'ar' | 'en' = 'en'): string {
  if (seconds < 60) {
    const formatted =
      lang === 'ar'
        ? Math.round(seconds)
            .toString()
            .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
        : Math.round(seconds).toString();
    return lang === 'ar' ? `${formatted} ث` : `${formatted}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);

  if (lang === 'ar') {
    const minsStr = minutes.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    const secsStr = remainingSeconds.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    return `${minsStr} د ${secsStr} ث`;
  } else {
    return `${minutes}m ${remainingSeconds}s`;
  }
}

/**
 * Get utilization level text
 *
 * @param rate - Utilization rate (0-100)
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized level text
 */
export function getUtilizationLevelActuator(rate: number, lang: 'ar' | 'en' = 'en'): string {
  let level: 'very-high' | 'high' | 'medium' | 'low' | 'very-low';

  if (rate >= 80) level = 'very-high';
  else if (rate >= 60) level = 'high';
  else if (rate >= 40) level = 'medium';
  else if (rate >= 20) level = 'low';
  else level = 'very-low';

  const translations = {
    'very-high': { ar: 'مرتفع جداً', en: 'Very High' },
    high: { ar: 'مرتفع', en: 'High' },
    medium: { ar: 'متوسط', en: 'Medium' },
    low: { ar: 'منخفض', en: 'Low' },
    'very-low': { ar: 'منخفض جداً', en: 'Very Low' },
  };

  return translations[level][lang];
}

/**
 * Format trigger source text
 *
 * @param trigger - Trigger source
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized trigger text
 */
export function formatTriggerActuator(
  trigger: 'manual' | 'schedule' | 'automation' | 'remote' | 'sensor',
  lang: 'ar' | 'en' = 'en',
): string {
  const translations = {
    manual: { ar: 'يدوي', en: 'Manual' },
    schedule: { ar: 'جدولة', en: 'Scheduled' },
    automation: { ar: 'أتمتة', en: 'Automated' },
    remote: { ar: 'عن بُعد', en: 'Remote' },
    sensor: { ar: 'مستشعر', en: 'Sensor-triggered' },
  };

  return translations[trigger]?.[lang] || trigger;
}

/**
 * Get health status text
 *
 * @param health - Health status
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized health text
 */
export function getHealthStatusText(
  health: 'good' | 'warning' | 'critical',
  lang: 'ar' | 'en' = 'en',
): string {
  const translations = {
    good: { ar: 'جيد', en: 'Good' },
    warning: { ar: 'تحذير', en: 'Warning' },
    critical: { ar: 'حرج', en: 'Critical' },
  };

  return translations[health]?.[lang] || health;
}

/**
 * Format utilization rate percentage
 *
 * @param rate - Utilization rate (0-100)
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted percentage string
 */
export function formatUtilizationActuator(rate: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? Math.round(rate)
          .toString()
          .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : Math.round(rate).toString();
  return lang === 'ar' ? `${formatted}٪` : `${formatted}%`;
}
